import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { trackByFactory } from '@mode/capra';
import { debounceTime, first, map, startWith, takeUntil } from 'rxjs/operators';
import { MatLegacyChipInputEvent } from '@angular/material/legacy-chips';

import { claimedDomainValidator } from './claimed-domain.validator';
import { ShareDialogEmailForm } from './share-dialog-email-form.interface';
import { EmailSettings } from '@mode/shared/ui';

enum ErrorMessage {
  invalidEmail = 'Invalid email',
  claimedDomain = 'Does not match Workspace claimed domain',
}

@Component({
  selector: 'mode-email-tab',
  templateUrl: './email-tab.component.html',
  styleUrls: ['../share-dialog.component.scss', './email-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formModel: { message: string; options: string[] } = { message: '', options: [] };
  @Input() schedulingEnabled = false;
  @Input() validEmailDomains: string[] = [];
  @Input() sharingTooltip = '';
  @Input() emailAttachmentsDisabled = false;
  @Input() sharingDisabled = false;
  @Input() sharing = false;
  @Output() sharingChange = new EventEmitter<boolean>();
  @Output() scheduleDelivery = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() sendEmail = new EventEmitter<ShareDialogEmailForm>();

  private _destroy$ = new Subject<void>();
  private _sharing$ = new BehaviorSubject<boolean>(false);
  private _recipientsExpanded$ = new BehaviorSubject<boolean>(false);
  private readonly _permittedMaxHeight = 160;
  public readonly maxRecipients = 500;
  public readonly ErrorMessage = ErrorMessage;
  public recipientErrors: Record<string, string> = {};
  public showInvalidEmailMessage = false;
  public showExternalEmailMessage = false;
  public showExternalEmailBanner = false;
  public emailOptions: readonly { name: string; value: string }[] = [
    {
      name: 'Preview of Report',
      value: 'data_previews_enabled',
    },
    {
      name: 'Table of query results',
      value: 'data_tables_enabled',
    },
    {
      name: 'Report and queries links',
      value: 'report_links_enabled',
    },
    {
      name: 'CSV file',
      value: 'csv_attachments_enabled',
    },
    {
      name: 'PDF file',
      value: 'pdf_attachments_enabled',
    },
  ];
  public emailForm = this._fb.group({
    recipients: this._fb.array([], Validators.required),
    message: this._fb.control(''),
    options: this._fb.control([]),
  });
  public showViewMoreButton$ = this.recipientsControl.valueChanges.pipe(
    startWith(false),
    debounceTime(0), // necessary for the following offsetHeight value check to read accurately
    map(() => (this.recipientList?.nativeElement?.offsetHeight ?? 0) >= this._permittedMaxHeight)
  );
  public currentMaxHeight$ = this._recipientsExpanded$.pipe(
    map((isOpen) => (isOpen ? 'none' : `${this._permittedMaxHeight}px`))
  );
  public viewButtonContent$ = this._recipientsExpanded$.pipe(
    map((isOpen) => (isOpen ? { direction: 'up', text: 'Show less' } : { direction: 'down', text: 'Show more' }))
  );

  @ViewChild('chipList', { read: ElementRef })
  public recipientList: ElementRef | undefined;

  public readonly recipientsTrackByFn = trackByFactory<AbstractControl>(({ value }) => value);

  public selections = { csv: false, pdf: false, previewImages: true, tablesOfRawData: false, urlLinks: false };

  constructor(private _fb: UntypedFormBuilder) {}

  public ngOnInit(): void {
    combineLatest([this._sharing$.asObservable(), this.recipientsControl.valueChanges])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([reportShared, recipients]) => {
        this.recipientErrors = this.recipientsControl.controls.reduce(
          (record, control) => {
            let errorMessage = '';
            if (control.hasError('email')) {
              errorMessage = ErrorMessage.invalidEmail;
            } else if (control.hasError('claimedDomain')) {
              errorMessage = ErrorMessage.claimedDomain;
            }
            record[control.value] = errorMessage;
            return record;
          },
          {} as Record<string, string>
        );

        this.showInvalidEmailMessage =
          !!recipients.length &&
          Object.values(this.recipientErrors).some((errorMessage) => errorMessage === ErrorMessage.invalidEmail);

        const someClaimedDomainError =
          !!recipients.length &&
          Object.values(this.recipientErrors).some((errorMessage) => errorMessage === ErrorMessage.claimedDomain);

        this.showExternalEmailMessage = someClaimedDomainError && !reportShared;
        this.showExternalEmailBanner = someClaimedDomainError && reportShared;
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (typeof changes?.sharing?.currentValue === 'boolean') {
      this._sharing$.next(changes.sharing.currentValue);
    }

    if (changes?.formModel?.firstChange) {
      const { message, options } = changes.formModel.currentValue;
      this.emailForm.patchValue({
        message,
        options,
      });
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public get recipientsControl(): UntypedFormArray {
    return this.emailForm.controls.recipients as UntypedFormArray;
  }

  public get messageControl(): UntypedFormControl {
    return this.emailForm.controls.message as UntypedFormControl;
  }

  public get optionsControl(): UntypedFormControl {
    return this.emailForm.controls.options as UntypedFormControl;
  }

  public addRecipient(event: MatLegacyChipInputEvent): void {
    this._addRecipient(event.value || '');
    event.chipInput?.clear();
  }

  public pasteRecipient(event: ClipboardEvent): void {
    event.preventDefault();

    if (!event.clipboardData) {
      return;
    }

    event.clipboardData
      .getData('text')
      .split(/;|,| |\n/) // splits value at semicolons, commas, spaces, or newlines
      .forEach((value) => this._addRecipient(value));
  }

  public removeRecipient(recipient: string): void {
    const index = this.recipientsControl.value.indexOf(recipient);
    if (index >= 0) {
      this.recipientsControl.removeAt(index);
    }
  }

  public toggleOption(checked: boolean, value: string): void {
    const valueIncluded = this.optionsControl.value.includes(value);
    if (checked && !valueIncluded) {
      this.optionsControl.setValue([...this.optionsControl.value, value]);
    } else if (!checked && valueIncluded) {
      this.optionsControl.setValue(this.optionsControl.value.filter((option: string) => option !== value));
    }
  }

  public toggleRecipientsExpanded(): void {
    this._recipientsExpanded$.pipe(first()).subscribe((state) => this._recipientsExpanded$.next(!state));
  }

  public toggleEmailSettings(newSettings: EmailSettings) {
    this.selections = newSettings;
    for (const [checked, name] of this.formatForBackend(newSettings)) {
      this.toggleOption(name, checked);
    }
  }

  private formatForBackend(newSettings: EmailSettings) {
    return Object.entries({
      data_previews_enabled: newSettings.previewImages,
      data_tables_enabled: newSettings.tablesOfRawData,
      report_links_enabled: newSettings.urlLinks,
      csv_attachments_enabled: newSettings.csv,
      pdf_attachments_enabled: newSettings.pdf,
    });
  }

  private _addRecipient(recipient: string): void {
    const trimmedRecipient = recipient.trim();
    if (
      trimmedRecipient &&
      this.recipientsControl.length < this.maxRecipients &&
      !(this.recipientsControl.value as string[]).includes(trimmedRecipient)
    ) {
      const recipientControl = this._fb.control(recipient, [
        Validators.email,
        claimedDomainValidator(this.validEmailDomains),
      ]);
      this.recipientsControl.push(recipientControl);
    }
  }
}
